import { useEffect, useState } from "react";
import api from "@/lib/api";
import { motion, AnimatePresence } from "framer-motion";
import { Check, RotateCcw, Sparkles, Shuffle } from "lucide-react";
import { toast } from "sonner";
import AdSlot from "@/components/AdSlot";

const RATINGS = [
  { key: "again", label: "Again", color: "#FCA5A5", bg: "rgba(252, 165, 165, 0.10)", bg2: "rgba(252, 165, 165, 0.25)" },
  { key: "hard", label: "Hard", color: "#B8A4FF", bg: "rgba(184, 164, 255, 0.10)", bg2: "rgba(184, 164, 255, 0.25)" },
  { key: "good", label: "Good", color: "#7C5CFF", bg: "rgba(124, 92, 255, 0.10)", bg2: "rgba(124, 92, 255, 0.25)" },
  { key: "easy", label: "Easy", color: "#5AC8FA", bg: "rgba(90, 200, 250, 0.10)", bg2: "rgba(90, 200, 250, 0.25)" },
];

export default function FlashcardReview({ subcourseId, onComplete }) {
  const [cards, setCards] = useState(null);
  const [idx, setIdx] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [done, setDone] = useState(false);
  const [xpBurst, setXpBurst] = useState(null);
  const [rated, setRated] = useState(0);
  const [shuffled, setShuffled] = useState(false);

  const shuffle = () => {
    setCards((cs) => {
      const head = cs.slice(0, idx);
      const rest = cs.slice(idx);
      for (let i = rest.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [rest[i], rest[j]] = [rest[j], rest[i]];
      }
      return [...head, ...rest];
    });
    setFlipped(false);
    setShuffled(true);
    toast.success("Remaining cards shuffled");
  };

  const url = subcourseId ? `/subcourses/${subcourseId}/flashcards` : `/flashcards/due`;

  const load = () => {
    api.get(url, { params: { due_only: subcourseId ? true : undefined, limit: 50 } }).then((r) => {
      setCards(r.data);
      setIdx(0);
      setFlipped(false);
      setDone(r.data.length === 0);
    });
  };
  useEffect(() => { load(); }, [subcourseId]);

  useEffect(() => {
    const h = (e) => {
      if (done || !cards || cards.length === 0) return;
      if (e.code === "Space") { e.preventDefault(); setFlipped((f) => !f); return; }
      if (!flipped) return;
      const m = { Digit1: "again", Digit2: "hard", Digit3: "good", Digit4: "easy" };
      if (m[e.code]) rate(m[e.code]);
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  });

  const rate = async (rating) => {
    if (!cards || !cards[idx]) return;
    const card = cards[idx];
    try {
      const { data } = await api.post(`/flashcards/${card.id}/rate`, { rating });
      setXpBurst({ amount: data.xp_awarded, due: data.next_due_in_days });
      setTimeout(() => setXpBurst(null), 1200);
      setRated((r) => r + 1);
    } catch {
      toast.error("Couldn't save");
      return;
    }
    if (idx + 1 >= cards.length) {
      setDone(true);
      onComplete && onComplete();
    } else {
      setIdx((i) => i + 1);
      setFlipped(false);
    }
  };

  if (cards === null) return <div className="h-96 shimmer rounded-2xl" />;

  if (cards.length === 0 || done) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="surface p-12 text-center max-w-xl mx-auto relative overflow-hidden"
      >
        <div className="absolute -top-20 -right-20 w-48 h-48 bg-gradient-to-br from-[#5AC8FA]/30 to-[#5AC8FA]/30 blur-3xl rounded-full" />
        <div className="relative">
          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: "spring", stiffness: 280, damping: 18, delay: 0.1 }}
            className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#5AC8FA] to-[#5AC8FA] flex items-center justify-center mx-auto mb-4 shadow-lg"
          >
            <Check size={28} className="text-white" strokeWidth={2.6} />
          </motion.div>
          <h3 className="display-3 mb-2">
            {cards.length === 0 ? "All caught up" : (
              <>
                Nice — <AnimatedRated n={rated} /> reviewed
              </>
            )}
          </h3>
          <p className="text-[15px] text-[#B0AEBC] mb-7 max-w-md mx-auto">
            {cards.length === 0
              ? "Your queue is empty. Come back tomorrow or compose a new study."
              : "Streak banked. XP added. See you tomorrow."}
          </p>
          <button onClick={load} className="btn btn-secondary" data-testid="fc-restart-btn">
            <RotateCcw size={13} /> Reload
          </button>
          <AdSlot className="rounded-2xl mt-8 text-left" testId="review-done-ad" />
        </div>
      </motion.div>
    );
  }

  const card = cards[idx];

  return (
    <div className="relative max-w-2xl mx-auto">
      <div className="flex items-center justify-between mb-3 text-[12px] text-[#B0AEBC]">
        <span className="font-semibold">Card {idx + 1} of {cards.length}</span>
        <div className="flex items-center gap-3">
          <button onClick={shuffle} disabled={cards.length - idx < 2}
            className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full border transition-colors disabled:opacity-40 ${shuffled ? "border-[#7C5CFF]/60 text-[#B8A4FF]" : "border-white/10 hover:text-[#B8A4FF] hover:border-[#B8A4FF]/50"}`}
            aria-label="Shuffle remaining cards" data-testid="fc-shuffle-btn">
            <Shuffle size={12} /> Shuffle
          </button>
          <span className="tabular-nums">{rated} rated</span>
        </div>
      </div>
      <div className="h-1.5 progress-track mb-7">
        <motion.div
          className="h-full progress-fill"
          initial={{ width: 0 }}
          animate={{ width: `${((idx + 1) / cards.length) * 100}%` }}
          transition={{ duration: 0.5 }}
        />
      </div>

      <div
        className="relative w-full cursor-pointer"
        style={{ height: 340, perspective: 1600 }}
        onClick={() => setFlipped((f) => !f)}
        data-testid="flashcard"
      >
        <AnimatePresence mode="wait" initial={false}>
          {!flipped ? (
            <motion.div
              key={`front-${idx}`}
              initial={{ rotateY: 90, opacity: 0 }}
              animate={{ rotateY: 0, opacity: 1 }}
              exit={{ rotateY: -90, opacity: 0 }}
              transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
              className="surface absolute inset-0 p-10 flex items-center justify-center text-center"
            >
              <div className="max-w-md">
                <div className="text-[11px] font-bold text-[#8E8B99] tracking-wider mb-5 uppercase">Front</div>
                <p className="text-[24px] font-semibold leading-tight tracking-tight" data-testid="card-front">
                  {card.front}
                </p>
                {card.hint && (
                  <p className="text-[13px] mt-5 grad-text font-medium">Hint: {card.hint}</p>
                )}
              </div>
            </motion.div>
          ) : (
            <motion.div
              key={`back-${idx}`}
              initial={{ rotateY: -90, opacity: 0 }}
              animate={{ rotateY: 0, opacity: 1 }}
              exit={{ rotateY: 90, opacity: 0 }}
              transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
              className="absolute inset-0 p-10 flex items-center justify-center text-center text-white rounded-[18px] overflow-hidden"
              style={{ background: "linear-gradient(135deg, #7C5CFF 0%, #B8A4FF 50%, #7C5CFF 100%)" }}
            >
              <div className="absolute inset-0 grid-lines opacity-15" />
              <div className="max-w-md relative">
                <div className="text-[11px] font-bold opacity-80 tracking-wider mb-5 uppercase">Answer</div>
                <p className="text-[18px] leading-relaxed font-medium" data-testid="card-back">{card.back}</p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <div className="text-center text-[12px] text-[#8E8B99] mt-4">
        {flipped ? "Rate your recall · 1/2/3/4" : "Tap or press Space to reveal"}
      </div>

      <div className="grid grid-cols-4 gap-2 mt-7">
        {RATINGS.map((r, i) => (
          <motion.button
            key={r.key}
            onClick={() => flipped && rate(r.key)}
            disabled={!flipped}
            whileHover={flipped ? { y: -4 } : {}}
            whileTap={flipped ? { scale: 0.95 } : {}}
            data-testid={`rate-${r.key}`}
            className="rounded-2xl py-4 px-2 transition-all disabled:opacity-30 disabled:cursor-not-allowed border"
            style={{
              background: flipped ? r.bg : "rgba(255,255,255,0.04)",
              borderColor: flipped ? r.bg2 : "rgba(255,255,255,0.08)",
              boxShadow: flipped ? `0 0 24px -8px ${r.color}` : "none",
            }}
          >
            <div className="text-[10px] font-bold text-[#8E8B99] mb-0.5">{i + 1}</div>
            <div className="font-bold text-[15px]" style={{ color: flipped ? r.color : "#71717A" }}>
              {r.label}
            </div>
          </motion.button>
        ))}
      </div>

      <AnimatePresence>
        {xpBurst && (
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.8 }}
            animate={{ opacity: 1, y: -40, scale: 1 }}
            exit={{ opacity: 0, y: -100, scale: 0.9 }}
            transition={{ type: "spring", stiffness: 280, damping: 20 }}
            className="fixed top-1/2 left-1/2 -translate-x-1/2 pointer-events-none z-50"
          >
            <div
              className="text-white text-[20px] font-bold px-6 py-3 rounded-full shadow-2xl flex items-center gap-2"
              style={{ background: "linear-gradient(135deg, #7C5CFF 0%, #B8A4FF 100%)", boxShadow: "0 16px 50px -10px rgba(0, 122, 255, 0.5)" }}
            >
              <Sparkles size={17} /> +{xpBurst.amount} XP
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

const AnimatedRated = ({ n }) => (
  <motion.span
    initial={{ scale: 0.6, opacity: 0 }}
    animate={{ scale: 1, opacity: 1 }}
    transition={{ type: "spring", stiffness: 220, damping: 14, delay: 0.2 }}
    className="grad-text tabular-nums"
  >
    {n}
  </motion.span>
);
